<template>
  <div class="dict-sidenav" :class="{ 'is-collapsed': collapsed }">
    <!-- Header -->
    <div class="dict-sidenav-header">
      <div v-if="!collapsed" class="dict-header-title-wrap">
        <div class="dict-header-icon">
          <i class="bi bi-journal-bookmark-fill"></i>
        </div>
        <div class="dict-header-text">
          <h6 class="dict-header-title">{{ dictTitle }}</h6>
          <span class="dict-header-sub">{{ totalCount }} {{ totalLabel }}</span>
        </div>
      </div>
      <button
        type="button"
        class="dict-collapse-btn"
        :title="collapsed ? 'Genişləndir' : 'Yığcamlaşdır'"
        :aria-label="collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
        @click="$emit('toggle-collapse')"
      >
        <i :class="collapsed ? 'bi bi-layout-sidebar' : 'bi bi-layout-sidebar-inset'"></i>
      </button>
    </div>

    <!-- Categorized Menu Navigation -->
    <div class="dict-nav-body custom-scrollbar">
      <div
        v-for="cat in categories"
        :key="cat.id"
        class="dict-category-group"
      >
        <!-- Category Title (When Expanded) -->
        <div v-if="!collapsed" class="dict-category-header">
          <span class="dict-category-name">{{ cat.title }}</span>
          <span class="dict-category-badge">{{ cat.items.length }}</span>
        </div>
        <div v-else class="dict-category-divider"></div>

        <!-- Category Items -->
        <ul class="dict-items-list">
          <li v-for="item in cat.items" :key="item.key" class="dict-item">
            <el-tooltip
              v-if="collapsed"
              :content="item.label"
              placement="right"
              :show-after="150"
            >
              <button
                type="button"
                class="dict-item-btn"
                :class="{ 'is-active': activeItem === item.key }"
                @click="selectItem(item.key)"
              >
                <span class="dict-item-icon-box" :class="item.colorClass">
                  <i :class="item.icon"></i>
                </span>
              </button>
            </el-tooltip>

            <button
              v-else
              type="button"
              class="dict-item-btn"
              :class="{ 'is-active': activeItem === item.key }"
              @click="selectItem(item.key)"
            >
              <span class="dict-item-icon-box" :class="item.colorClass">
                <i :class="item.icon"></i>
              </span>
              <span class="dict-item-label">{{ item.label }}</span>
              <i v-if="activeItem === item.key" class="bi bi-chevron-right dict-item-arrow"></i>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DictionarySideNav",
  props: {
    activeItem: {
      type: String,
      default: "Conditions"
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  emits: ["toggle-dictionary", "toggle-collapse"],
  computed: {
    dictTitle() {
      return "Soraqçalar";
    },
    totalLabel() {
      return "bölmə";
    },
    categories() {
      return [
        {
          id: "main",
          title: "Əsas Bölmələr",
          items: [
            {
              key: "Conditions",
              label: "Şərtlər",
              icon: "bi bi-file-earmark-text",
              colorClass: "icon--primary"
            },
            {
              key: "Illness",
              label: "Xəstəliklər",
              icon: "bi bi-heart-pulse",
              colorClass: "icon--danger"
            },
            {
              key: "Symptoms",
              label: "Simptomlar",
              icon: "bi bi-activity",
              colorClass: "icon--warning"
            }
          ]
        },
        {
          id: "templates",
          title: "Qaydalar və Şablonlar",
          items: [
            {
              key: "RejectType",
              label: "İmtina səbəbi",
              icon: "bi bi-x-circle",
              colorClass: "icon--danger"
            },
            {
              key: "ComplaintTemplate",
              label: "Şikayətlər",
              icon: "bi bi-exclamation-triangle",
              colorClass: "icon--warning"
            },
            {
              key: "ExceptionType",
              label: "İstisnalar",
              icon: "bi bi-shield-slash",
              colorClass: "icon--dark"
            }
          ]
        }
      ];
    },
    totalCount() {
      return this.categories.reduce((acc, cat) => acc + cat.items.length, 0);
    }
  },
  methods: {
    selectItem(key) {
      this.$emit("toggle-dictionary", key);
    }
  }
};
</script>

<style scoped lang="scss">
.dict-sidenav {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: calc(100vh - 140px);
  background: var(--app-surface, #ffffff);
  border: 1px solid var(--app-border, #e2e8f0);
  border-radius: var(--app-radius, 12px);
  box-shadow: var(--app-shadow, 0 1px 3px rgba(0, 0, 0, 0.05));
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.dict-sidenav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid var(--app-border, #e2e8f0);
  background: var(--app-surface-soft, #f8fafc);
  gap: 0.5rem;
}

.dict-header-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.dict-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: color-mix(in srgb, var(--bs-info, #0b4f9c) 14%, var(--app-surface, #ffffff));
  color: var(--bs-info, #0b4f9c);
  font-size: 0.95rem;
  flex-shrink: 0;
}

.dict-header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.dict-header-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--app-heading, #1e293b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dict-header-sub {
  font-size: 0.7rem;
  color: var(--app-text, #64748b);
}

.dict-collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--app-border, #e2e8f0);
  border-radius: 6px;
  background: var(--app-surface, #ffffff);
  color: var(--app-text, #64748b);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    color: var(--bs-info, #0b4f9c);
    border-color: color-mix(in srgb, var(--bs-info, #0b4f9c) 30%, var(--app-border, #e2e8f0));
    background: var(--app-surface-tint, #f0f7ff);
  }
}

.dict-nav-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.6rem 0.5rem 1rem;
  background: var(--app-surface, #ffffff);
}

.dict-category-group {
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.dict-category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.45rem 0.25rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--app-text, #64748b);
  opacity: 0.85;
}

.dict-category-badge {
  font-size: 0.62rem;
  padding: 0.05rem 0.35rem;
  border-radius: 999px;
  background: var(--app-surface-muted, #f1f5f9);
  color: var(--app-text, #64748b);
}

.dict-category-divider {
  height: 1px;
  background: var(--app-border, #e2e8f0);
  margin: 0.4rem 0.25rem;
}

.dict-items-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.dict-item-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.45rem 0.55rem;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--app-heading, #1e293b);
  text-align: left;
  cursor: pointer;
  gap: 0.6rem;
  transition: all 0.15s ease;

  &:hover {
    background: var(--app-surface-tint, #f0f7ff);
    color: var(--bs-info, #0b4f9c);

    .dict-item-arrow {
      transform: translateX(2px);
      opacity: 1;
    }
  }

  &.is-active {
    background: color-mix(in srgb, var(--bs-info, #0b4f9c) 10%, var(--app-surface, #ffffff));
    border-color: color-mix(in srgb, var(--bs-info, #0b4f9c) 25%, var(--app-border, #e2e8f0));
    color: var(--bs-info, #0b4f9c);
    font-weight: 650;
    box-shadow: 0 1px 2px rgba(11, 79, 156, 0.06);

    .dict-item-icon-box {
      background: var(--bs-info, #0b4f9c);
      color: #ffffff;
      box-shadow: 0 2px 6px color-mix(in srgb, var(--bs-info, #0b4f9c) 30%, transparent);
    }
  }
}

.dict-item-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  font-size: 0.82rem;
  flex-shrink: 0;
  background: var(--app-surface-soft, #f8fafc);
  color: var(--app-text, #64748b);
  transition: all 0.15s ease;

  &.icon--primary {
    color: var(--bs-info, #0b4f9c);
    background: color-mix(in srgb, var(--bs-info, #0b4f9c) 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--danger {
    color: #dc2626;
    background: color-mix(in srgb, #dc2626 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--info {
    color: #0284c7;
    background: color-mix(in srgb, #0284c7 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--success {
    color: #059669;
    background: color-mix(in srgb, #059669 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--warning {
    color: #d97706;
    background: color-mix(in srgb, #d97706 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--teal {
    color: #0d9488;
    background: color-mix(in srgb, #0d9488 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--purple {
    color: #7c3aed;
    background: color-mix(in srgb, #7c3aed 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--indigo {
    color: #4f46e5;
    background: color-mix(in srgb, #4f46e5 12%, var(--app-surface-soft, #f8fafc));
  }
  &.icon--dark {
    color: #475569;
    background: color-mix(in srgb, #475569 12%, var(--app-surface-soft, #f8fafc));
  }
}

.dict-item-label {
  font-size: 0.82rem;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
}

.dict-item-arrow {
  font-size: 0.65rem;
  color: var(--bs-info, #0b4f9c);
  opacity: 0.7;
  transition: transform 0.15s ease, opacity 0.15s ease;
  flex-shrink: 0;
}

/* Collapsed sidebar mode */
.is-collapsed {
  .dict-sidenav-header {
    justify-content: center;
    padding: 0.75rem 0.35rem;
  }

  .dict-nav-body {
    padding: 0.4rem 0.25rem;
  }

  .dict-item-btn {
    justify-content: center;
    padding: 0.4rem;
  }
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--app-border, #e2e8f0) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--app-border, #e2e8f0);
    border-radius: 4px;
  }
}

[data-theme="dark"] {
  .dict-sidenav {
    background: #1c232b;
    border-color: #2e3846;
  }

  .dict-sidenav-header {
    background: #182029;
    border-color: #2e3846;
  }

  .dict-header-title {
    color: #f1f5f9;
  }

  .dict-collapse-btn {
    background: #252f3b;
    border-color: #334155;
    color: #94a3b8;

    &:hover {
      background: #2e3b4b;
      color: #60a5fa;
    }
  }

  .dict-nav-body {
    background: #1c232b;
  }

  .dict-category-divider {
    background: #2e3846;
  }

  .dict-category-badge {
    background: #252f3b;
    color: #94a3b8;
  }

  .dict-item-btn {
    color: #e2e8f0;

    &:hover {
      background: #252f3b;
      color: #93c5fd;
    }

    &.is-active {
      background: rgba(11, 79, 156, 0.28);
      border-color: rgba(96, 165, 250, 0.4);
      color: #93c5fd;

      .dict-item-icon-box {
        background: #2563eb;
        color: #ffffff;
      }
    }
  }
}
</style>
